import { ApolloClient } from "@apollo/client";
import type { ApolloQueryResult } from "@apollo/client/core/types";
import {
  ConditionCategory as ConditionCategoryEnum,
  ConditionOperator as ConditionOperatorEnum,
  ConditionType as ConditionTypeEnum,
  ConditionValueType,
  RateType as RateTypeEnum,
  UnitType as UnitTypeEnum,
} from "@sinch/core/entities/serverEnums";
import { RatelistType } from "@sinch/core/entities/serverEnums.types";
import { ConditionCategory, ConditionOperator, type ConditionType, RateType, UnitType } from "@sinch/types/sinch.types";
import { t } from "i18next";
import { isNil, mapObjIndexed } from "ramda";
import { isArray, isNotNil, isNotNilOrEmpty } from "ramda-adjunct";
import { FieldPath } from "react-hook-form";
import { array, boolean, InferType, lazy, mixed, number, object, string, StringSchema, ValidationError } from "yup";
import { RatelistUnits } from "@sinch/components/application/Rate/queries.graphql";
import { RatelistUnitsQuery, RatelistUnitsQueryVariables } from "@sinch/components/application/Rate/queries.types";
import { isBreakPenaltySourcedComponent, RateUnitValue } from "@sinch/components/application/Rate/useIntervalType";
import {
  ConditionCategories,
  RateConditionPossibleValues,
  RateConditions,
} from "@sinch/components/application/RatelistBuilder/RateBuilderRowContent/ConditionInput/queries.graphql";
import {
  ConditionCategoriesQuery,
  ConditionCategoriesQueryVariables,
  RateConditionPossibleValuesQuery,
  RateConditionPossibleValuesQueryVariables,
  RateConditionsQuery,
  RateConditionsQueryVariables,
} from "@sinch/components/application/RatelistBuilder/RateBuilderRowContent/ConditionInput/queries.types";
import { RateIntervals } from "@sinch/components/application/RatelistBuilder/RateBuilderRowContent/IntervalSelect/queries.graphql";
import {
  RateIntervalsQuery,
  RateIntervalsQueryVariables,
} from "@sinch/components/application/RatelistBuilder/RateBuilderRowContent/IntervalSelect/queries.types";
import { RateBuilderTreeViewItem } from "@sinch/components/application/RatelistBuilder/types";

// ─── Validation context ───────────────────────────────────────────────────────

/** Yup validation context used by these schemas. */
type ValidationCtx = { apolloClient: ApolloClient<any>; ratelistType: RatelistType };

type ConditionParent = {
  category: ConditionCategory | ConditionCategoryEnum | null;
  operator: ConditionOperator | ConditionOperatorEnum | null;
  subjectId: string | null;
  type: ConditionType | ConditionTypeEnum | null;
};

// ─── Cache ────────────────────────────────────────────────────────────────────

/** Simple in-memory caches to avoid repeated network calls during validation. */
const rateConditionCategoriesCache = new Map<string, ConditionCategoriesQuery["rateConditionCategories"]>();
const rateConditionsCache = new Map<string, RateConditionsQuery["rateConditions"]>();
const possibleValuesCache = new Map<string, (string | number | Date)[]>();
const ratelistUnitsCache = new Map<RatelistType, RatelistUnitsQuery["ratelistUnits"]>();

const cacheKeyRateConditionCategories = (ratelistType: RatelistType) => `rcc:${ratelistType}`;
const cacheKeyRateConditions = (category: ConditionCategory) => `rc:${category}`;
const cacheKeyPossibleValues = (category: ConditionCategory, subjectId: string | null, type: ConditionType) =>
  `pv:${category}:${subjectId ?? "null"}:${type}`;

// ─── Data loaders ─────────────────────────────────────────────────────────────

type QueryFn = ApolloClient<any>["query"];

const loadRateConditions = async (
  query: QueryFn,
  category: ConditionCategory
): Promise<RateConditionsQuery["rateConditions"]> => {
  const key = cacheKeyRateConditions(category);
  const cached = rateConditionsCache.get(key);
  if (cached) return cached;

  const res: ApolloQueryResult<RateConditionsQuery> = await query<RateConditionsQuery, RateConditionsQueryVariables>({
    query: RateConditions,
    variables: { category },
  });
  rateConditionsCache.set(key, res.data.rateConditions);
  return res.data.rateConditions;
};

const loadRateConditionCategories = async (
  query: QueryFn,
  ratelistType: RatelistType
): Promise<ConditionCategoriesQuery["rateConditionCategories"]> => {
  const key = cacheKeyRateConditionCategories(ratelistType);
  const cached = rateConditionCategoriesCache.get(key);
  if (cached) return cached;

  const res: ApolloQueryResult<ConditionCategoriesQuery> = await query<
    ConditionCategoriesQuery,
    ConditionCategoriesQueryVariables
  >({
    query: ConditionCategories,
    variables: { ratelistType },
  });
  rateConditionCategoriesCache.set(key, res.data.rateConditionCategories);
  return res.data.rateConditionCategories;
};

const fetchAllowedCategories = async (query: QueryFn, ratelistType: RatelistType): Promise<string[]> => {
  const categories = await loadRateConditionCategories(query, ratelistType);
  return categories.map(({ category }) => category).filter(isNotNil);
};

const loadRatelistUnits = async (
  query: QueryFn,
  ratelistType: RatelistType
): Promise<RatelistUnitsQuery["ratelistUnits"]> => {
  const cached = ratelistUnitsCache.get(ratelistType);
  if (cached) return cached;

  const res: ApolloQueryResult<RatelistUnitsQuery> = await query<RatelistUnitsQuery, RatelistUnitsQueryVariables>({
    query: RatelistUnits,
    variables: { ratelistType },
  });
  ratelistUnitsCache.set(ratelistType, res.data.ratelistUnits);
  return res.data.ratelistUnits;
};

const fetchIntervalsTypes = async (query: QueryFn, ratelistType: RatelistType): Promise<Map<string, string | null>> => {
  const units = await loadRatelistUnits(query, ratelistType);
  return new Map(units.map((unit) => [unit.unitType, unit.unitName]));
};

/**
 * Custom intervals available to the ratelist, with their type and settings.
 *
 * Deliberately uncached here - Apollo's normalized cache already serves this query, and creating an
 * interval refetches it, so a private cache would go stale. `ratelistType` is optional on the query,
 * an undefined one simply returns every interval, which is all a lookup by id needs.
 */
const loadRateIntervals = async (
  query: QueryFn,
  ratelistType: RatelistType | undefined
): Promise<RateIntervalsQuery["rateIntervals"]> => {
  const res: ApolloQueryResult<RateIntervalsQuery> = await query<RateIntervalsQuery, RateIntervalsQueryVariables>({
    query: RateIntervals,
    variables: { ratelistType },
  });
  return res.data.rateIntervals;
};

const fetchAllowedSubjects = async (query: QueryFn, category: ConditionCategory): Promise<string[]> => {
  const subjects = await loadRateConditions(query, category);
  return subjects.map(({ subjectId }) => subjectId).filter(isNotNil);
};

const fetchConditionPossibleValues = async (
  query: QueryFn,
  category: ConditionCategory,
  subjectId: string | null,
  type: ConditionType
): Promise<(string | number | Date)[] | null> => {
  const subjects = await loadRateConditions(query, category);
  const valueType = subjects.find((r) => r.subjectId === subjectId && r.type === type)?.valueType;

  if (valueType !== ConditionValueType.Select) {
    return null; // no restrictions for non-select types
  }

  const key = cacheKeyPossibleValues(category, subjectId, type);
  const cached = possibleValuesCache.get(key);
  if (cached) return cached;

  const res: ApolloQueryResult<RateConditionPossibleValuesQuery> = await query<
    RateConditionPossibleValuesQuery,
    RateConditionPossibleValuesQueryVariables
  >({
    query: RateConditionPossibleValues,
    variables: { category, subjectId, type },
  });

  const allowed = res.data.rateConditionPossibleValues.map(({ value }) => value).filter(isNotNil);
  possibleValuesCache.set(key, allowed);
  return allowed;
};

// ─── Reusable test helpers ────────────────────────────────────────────────────

const getApolloClient = (options: { context?: ValidationCtx }): ApolloClient<any> => {
  const client = options.context?.apolloClient;
  if (!client) throw new Error("Missing validation context");
  return client;
};

const notNullTest = (name: string) => ({
  name,
  message: () => t("required"),
  test: (value: unknown) => value !== null,
});

// ─── Schemas ──────────────────────────────────────────────────────────────────

export const rateConditionSchema = object().shape({
  category: string<ConditionCategory | ConditionCategoryEnum>()
    .defined()
    .nullable()
    .default(null)
    .test(notNullTest("not-null"))
    .test(
      "in-list",
      () => t("Error.someOptionsAreUnavailable"),
      async (value, { options }) => {
        if (isNil(value)) return true; // let required handle empties
        const client = getApolloClient(options as { context?: ValidationCtx });
        const allowed = await fetchAllowedCategories(client.query, options.context?.ratelistType);
        return allowed.includes(value);
      }
    ),

  operator: string<ConditionOperator | ConditionOperatorEnum>().nullable().defined().default(null),

  // Cast to ValidationCtx so context is typed inside the test
  subjectId: (string() as StringSchema<string | null, ValidationCtx>)
    .nullable()
    .optional()
    .default(null)
    .test(
      "in-list",
      () => t("Error.someOptionsAreUnavailable"),
      async (value, { options, parent }) => {
        if (isNil(value) || isNil(parent?.category)) return true; // let required handle empties
        const client = getApolloClient(options);
        const allowed = await fetchAllowedSubjects(client.query, parent.category as ConditionCategory);
        return allowed.includes(value);
      }
    ),

  type: string<ConditionType | ConditionTypeEnum>().nullable().defined().default(null),

  value: mixed<string | number | Date | (string | number | Date)[]>()
    .nullable()
    .defined()
    .default(null)
    .test(
      "in-list",
      () => t("Error.someOptionsAreUnavailable"),
      async (value, { options, parent }) => {
        const p = parent as ConditionParent;
        if (isNil(value) || isNil(p.category) || isNil(p.type)) return true; // let required handle empties
        const client = getApolloClient(options as { context?: ValidationCtx });

        const allowed = await fetchConditionPossibleValues(
          client.query,
          p.category as ConditionCategory,
          p.subjectId,
          p.type as ConditionType
        );
        if (isNil(allowed)) return true; // no restrictions for non-select

        // For array values, every item must be allowed; for scalars, the value itself must be allowed.
        return isArray(value)
          ? (value as (string | number | Date)[]).every((v) => allowed.includes(v))
          : allowed.includes(value as string | number | Date);
      }
    )
    .test(
      "is-empty",
      () => t("Error.optionValueEmpty"),
      (value, { parent }) => {
        const p = parent as ConditionParent;
        if (isNil(p.category)) return true;
        return (
          p.operator === ConditionOperatorEnum.Exist ||
          p.operator === ConditionOperatorEnum.NotExist ||
          isNotNilOrEmpty(value)
        );
      }
    ),
});

export type RateConditionSchemaFields = InferType<typeof rateConditionSchema>;

export const rateRateSchema = object().shape({
  unit: object().shape({
    customIntervalId: string().nullable().default(null),
    priority: number().nullable().default(null),
    type: string<UnitType | UnitTypeEnum>().nullable().defined().default(null).test(notNullTest("not-null")),
  }),
  minimum: object()
    .shape({
      amount: number().default(0),
      overrideWorkedHours: boolean().default(false),
      //limitWithWorkTime: boolean().default(false),
    })
    .nullable()
    .default({ amount: 0, overrideWorkedHours: false }),
  baseComponent: string().nullable().default(null),
  // Overrides the ratelist and the instance default rounding for this rule only
  roundingId: string().nullable().default(null),
  amount: number()
    .nullable()
    .when({ is: (val: number | null) => isNil(val), then: (schema) => schema.required() })
    .default(0),
  type: string<RateType | RateTypeEnum>().defined().default(RateTypeEnum.Simple),
  // The message must stay lazy - this schema is built at module load, before i18next is initialized,
  // so an eagerly resolved t() would leave yup with its untranslated "<path> is a required field" default.
  component: string()
    .defined()
    .nullable()
    .default(null)
    .test(
      "required-unless-calculated",
      () => t("required"),
      async (value, { options, parent }) => {
        if (isNotNil(value)) return true;

        // A break penalty interval that reports under the component it is valued from calculates the
        // row's component, so the select is never rendered and must not be required.
        const client = getApolloClient(options as { context?: ValidationCtx });
        const intervals = await loadRateIntervals(client.query, options.context?.ratelistType);

        return isBreakPenaltySourcedComponent(intervals, (parent as { unit?: RateUnitValue })?.unit);
      }
    ),
});

export const rateItemSchema = (disableCondition?: boolean) =>
  object()
    .shape({
      name: string().nullable().default(""),
      condition: rateConditionSchema
        .nullable()
        .defined()
        .default(disableCondition ? null : rateConditionSchema.getDefault()),
      rate: rateRateSchema.nullable().defined().default(rateRateSchema.getDefault()),
    })
    .defined();

export type RateItemSchemaFormFieldsInfer = InferType<ReturnType<typeof rateItemSchema>>;

export const ratesTreeListSchema = (disableCondition?: boolean) =>
  lazy((value) =>
    object(mapObjIndexed(() => rateItemSchema(disableCondition), value))
      .required()
      .default({})
  );

type RateItemNullable = RateItemSchemaFormFieldsInfer | null | undefined;
type RateItemListForValidation = RateItemNullable[] | Record<string, RateItemNullable>;

export const checkIntervalTypeConflict = async function (
  values: RateItemListForValidation,
  apolloClient: ApolloClient<any>,
  ratelistType: RatelistType
) {
  const rateUnitTypeMap = await fetchIntervalsTypes(apolloClient.query, ratelistType);

  // Group rate keys by their component, collecting each entry's unit type.
  const componentGroups = new Map<string, { key: string; unitType: string | null | undefined }[]>();

  for (const [key, rateItem] of Object.entries(values)) {
    const item = rateItem as RateItemSchemaFormFieldsInfer | null | undefined;
    const component = item?.rate?.component;
    if (!component) continue;

    if (!componentGroups.has(component)) componentGroups.set(component, []);
    componentGroups
      .get(component)!
      .push({ key, unitType: item?.rate?.unit?.type ? (rateUnitTypeMap.get(item.rate.unit.type) ?? null) : null });
  }

  // Collect errors for every rate that belongs to a component with mixed interval types.
  const invalidFields: string[] = [];

  for (const [, group] of componentGroups) {
    const unitTypes = new Set(group.map((g) => g.unitType));
    if (unitTypes.size <= 1) continue;

    for (const { key } of group) {
      invalidFields.push(`${key}.rate.unit.type`);
    }
  }

  return invalidFields;
};

export const rateBuilderSchema = (disableCondition?: boolean) =>
  object()
    .shape({ Rate: ratesTreeListSchema(disableCondition) })
    .test("same-component-same-interval", "", async function (value, { options }) {
      if (!value?.Rate) return true;

      const client = getApolloClient(options as { context?: ValidationCtx });
      const basePath = this.path ? `${this.path}.` : "";

      const errors = (await checkIntervalTypeConflict(value.Rate, client, options.context?.ratelistType)).map((path) =>
        this.createError({
          path: `${basePath}Rate.${path}`,
          message: t("Error.sameComponentDifferentInterval"),
        })
      );

      return errors.length > 0 ? new ValidationError(errors) : true;
    });

export interface RateBuilderTreeFormFields {
  Rate: Record<string, RateItemSchemaFormFieldsInfer>;
}

export const builderSchema = object()
  .shape({
    changedFields: array().of(string<FieldPath<RateBuilderTreeFormFields>>().nonNullable().required()).required(),
    data: rateBuilderSchema(),
    expanded: array().of(string().required()).required(),
    tree: array().of(mixed<RateBuilderTreeViewItem>().required()).required(),
  })
  .nonNullable()
  .required();

export const historySchema = object().shape({
  past: array().of(builderSchema).required(),
  current: builderSchema,
  future: array().of(builderSchema).required(),
});
